/**
 * Technical enum values matching backend Prisma schema
 * These are the values that backend sends and expects
 */

// Lead Status (from LeadStatus enum in Prisma)
export enum LeadStatus {
  NEW = 'NEW',
  CONTACTED = 'CONTACTED',
  FOLLOW_UP = 'FOLLOW_UP',
  CONVERTED = 'CONVERTED',
  LOST = 'LOST',
}

// Company Status (from CompanyStatus enum in Prisma)
export enum CompanyStatus {
  NEW_CONTACT = 'NEW_CONTACT',
  CONTACTED = 'CONTACTED',
  ACTIVE_DIALOG = 'ACTIVE_DIALOG',
  COME_BACK_LATER = 'COME_BACK_LATER',
  CONNECTED = 'CONNECTED',
  NOT_INTERESTED = 'NOT_INTERESTED',
}

// Vehicle Card Status (from VehicleCardStatus enum in Prisma)
export enum VehicleCardStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  COMPLETED = 'COMPLETED',
  INCOMPLETE = 'INCOMPLETE',
}

// Vehicle Fuel Type (from VehicleFuelType enum in Prisma)
export enum VehicleFuelType {
  GASOLINE = 'GASOLINE',
  DIESEL = 'DIESEL',
  ELECTRIC = 'ELECTRIC',
  // Both were missing here while the backend enum and the locale files already had them,
  // so an ethanol or gas vehicle rendered its raw enum name (or a blank field) in the UI.
  ETHANOL = 'ETHANOL',
  HYBRID = 'HYBRID',
  PLUG_IN_HYBRID = 'PLUG_IN_HYBRID',
  GAS = 'GAS',
  OTHER = 'OTHER',
}

// Transmission Type (from TransmissionType enum in Prisma)
export enum TransmissionType {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
  SEMI_AUTOMATIC = 'SEMI_AUTOMATIC',
  CVT = 'CVT',
  OTHER = 'OTHER',
}

// Condition Grade (from ConditionGrade enum in Prisma)
export enum ConditionGrade {
  NEW = 'NEW',
  LIKE_NEW = 'LIKE_NEW',
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  // NORMAL, RENOVATION_OBJECT and OTHER exist in the backend's Prisma ConditionGrade enum
  // but were missing here, so those grades rendered as raw enum names in the UI.
  NORMAL = 'NORMAL',
  FAIR = 'FAIR',
  POOR = 'POOR',
  RENOVATION_OBJECT = 'RENOVATION_OBJECT',
  OTHER = 'OTHER',
}

/**
 * The condition grades offered in every "pick a condition" select, in display order.
 * Derived from the enum itself so a grade added above can never again be missing from a
 * dropdown (the two create-vehicle-card dialogs previously hardcoded six of the nine).
 * Declaration order of the enum is the display order.
 */
export const CONDITION_GRADE_OPTIONS: readonly ConditionGrade[] =
  Object.values(ConditionGrade);

// Vehicle Status (from VehicleStatus enum in Prisma)
export enum VehicleStatus {
  AVAILABLE = 'AVAILABLE',
  RESERVED = 'RESERVED',
  SOLD = 'SOLD',
  AUCTION = 'AUCTION',
  HIDDEN = 'HIDDEN',
}

// Type guards and helpers
export const isLeadStatus = (value: string): value is LeadStatus => {
  return Object.values(LeadStatus).includes(value as LeadStatus);
};

export const isVehicleCardStatus = (value: string): value is VehicleCardStatus => {
  return Object.values(VehicleCardStatus).includes(value as VehicleCardStatus);
};

export const isVehicleFuelType = (value: string): value is VehicleFuelType => {
  return Object.values(VehicleFuelType).includes(value as VehicleFuelType);
};

export const isTransmissionType = (value: string): value is TransmissionType => {
  return Object.values(TransmissionType).includes(value as TransmissionType);
};

export const isConditionGrade = (value: string): value is ConditionGrade => {
  return Object.values(ConditionGrade).includes(value as ConditionGrade);
};

